import api from './api/axiosConfig';

/** Normalize list payloads from catalogue APIs (array or paginated). */
export const normalizeCatalogueList = (response) => {
  if (Array.isArray(response)) {
    return response;
  }
  if (response && Array.isArray(response.results)) {
    return response.results;
  }
  return [];
};

const businessService = {
  // Get all businesses with optional filters
  getBusinesses: async (params = {}) => {
    try {
      const response = await api.get('/businesses/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching businesses:', error);
      throw error;
    }
  },

  // Get category counts for directory (approved, public only)
  getCategories: async () => {
    try {
      const response = await api.get('/businesses/categories/');
      const payload = response.data?.data ?? response.data;
      return Array.isArray(payload) ? payload : [];
    } catch (error) {
      console.error('Error fetching business categories:', error);
      return [];
    }
  },

  // Get a single business by ID
  getBusiness: async (id) => {
    try {
      const response = await api.get(`/businesses/${id}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching business:', error);
      throw error;
    }
  },

  // Create a new business
  createBusiness: async (businessData) => {
    try {
      const formData = new FormData();
      Object.keys(businessData).forEach((key) => {
        if (key === 'images' && Array.isArray(businessData[key])) {
          // Append images in order - this order must match face_image_index
          businessData[key].forEach((file) => formData.append('images_data', file));
        } else if (key === 'display_image_index') {
          // Handle display_image_index - convert to string if not null
          if (businessData[key] !== null && businessData[key] !== undefined) {
            formData.append(key, String(businessData[key]));
          }
        } else if (key === 'society' && businessData[key]) {
          // Ensure society is sent as string (FormData will handle conversion)
          formData.append(key, String(businessData[key]));
        } else if (key === 'is_public') {
          // Convert boolean to string for FormData
          formData.append(key, businessData[key] ? 'true' : 'false');
        } else if (businessData[key] !== undefined && businessData[key] !== null && businessData[key] !== '') {
          formData.append(key, businessData[key]);
        }
      });

      const response = await api.post('/businesses/', formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000, // 2 minutes for image uploads
      });
      return response.data;
    } catch (error) {
      console.error('Error creating business:', error);
      throw error;
    }
  },

  // Update a business
  updateBusiness: async (id, businessData) => {
    try {
      const formData = new FormData();
      Object.keys(businessData).forEach((key) => {
        if (key === 'images' && Array.isArray(businessData[key])) {
          // Append images in order - this order must match display_image_index
          businessData[key].forEach((file) => formData.append('images_data', file));
        } else if (key === 'display_image_index') {
          // Handle display_image_index - convert to string if not null
          if (businessData[key] !== null && businessData[key] !== undefined) {
            formData.append(key, String(businessData[key]));
          }
        } else if (key === 'society' && businessData[key]) {
          // Ensure society is sent as string (FormData will handle conversion)
          formData.append(key, String(businessData[key]));
        } else if (key === 'is_public') {
          // Convert boolean to string for FormData
          formData.append(key, businessData[key] ? 'true' : 'false');
        } else if (key === 'images_to_delete' && Array.isArray(businessData[key])) {
          // Handle images to delete
          businessData[key].forEach((imageId) => formData.append('images_to_delete', String(imageId)));
        } else if (businessData[key] !== undefined && businessData[key] !== null && businessData[key] !== '') {
          formData.append(key, businessData[key]);
        }
      });

      const response = await api.put(`/businesses/${id}/`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000, // 2 minutes for image uploads
      });
      return response.data;
    } catch (error) {
      console.error('Error updating business:', error);
      throw error;
    }
  },

  // Partially update a business (JSON PATCH — settings sections that edit text /
  // select / boolean fields: name, description, category, society, address,
  // is_public, contact_phone). Image/branding changes use updateBusiness
  // (multipart) instead. PATCH avoids PUT's full-object requirement so a section
  // can save only the fields it owns.
  patchBusiness: async (id, partialData) => {
    try {
      const response = await api.patch(`/businesses/${id}/`, partialData);
      return response.data;
    } catch (error) {
      console.error('Error patching business:', error);
      throw error;
    }
  },

  // Delete a business
  deleteBusiness: async (id) => {
    try {
      const response = await api.delete(`/businesses/${id}/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting business:', error);
      throw error;
    }
  },

  // Increment business views
  incrementViews: async (id) => {
    try {
      const response = await api.post(`/businesses/${id}/increment-views/`);
      return response.data;
    } catch (error) {
      console.error('Error incrementing views:', error);
      throw error;
    }
  },

  // Get products for a business
  getProducts: async (businessId, params = {}) => {
    try {
      const response = await api.get(`/businesses/${businessId}/products/`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  },

  // Get a single product
  getProduct: async (businessId, productId) => {
    try {
      const response = await api.get(`/businesses/${businessId}/products/${productId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching product:', error);
      throw error;
    }
  },

  // Create a new product. Uses multipart/form-data so image files attach correctly.
  createProduct: async (businessId, productData) => {
    try {
      const formData = new FormData();
      Object.keys(productData).forEach((key) => {
        const value = productData[key];
        if (key === 'images' && Array.isArray(value)) {
          value.forEach((file) => formData.append('images', file));
        } else if (key === 'images_to_delete' && Array.isArray(value)) {
          value.forEach((id) => formData.append('images_to_delete', id));
        } else if (key === 'is_available' || key === 'is_menu_item') {
          formData.append(key, value ? 'true' : 'false');
        } else if (value !== undefined && value !== null && value !== '') {
          formData.append(key, value);
        }
      });

      const response = await api.post(`/businesses/${businessId}/products/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      return response.data;
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  },

  // Update a product. Uses multipart/form-data to keep parity with create.
  updateProduct: async (businessId, productId, productData) => {
    try {
      const formData = new FormData();
      Object.keys(productData).forEach((key) => {
        const value = productData[key];
        if (key === 'images' && Array.isArray(value)) {
          value.forEach((file) => formData.append('images', file));
        } else if (key === 'images_to_delete' && Array.isArray(value)) {
          value.forEach((id) => formData.append('images_to_delete', id));
        } else if (key === 'is_available' || key === 'is_menu_item') {
          formData.append(key, value ? 'true' : 'false');
        } else if (value !== undefined && value !== null && value !== '') {
          formData.append(key, value);
        }
      });

      const response = await api.put(`/businesses/${businessId}/products/${productId}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      return response.data;
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  },

  // Partially update a product (JSON PATCH — e.g. inline availability/price edits)
  patchProduct: async (businessId, productId, partialData) => {
    try {
      const response = await api.patch(`/businesses/${businessId}/products/${productId}/`, partialData);
      return response.data;
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  },

  // Delete a product
  deleteProduct: async (businessId, productId) => {
    try {
      const response = await api.delete(`/businesses/${businessId}/products/${productId}/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  },

  // Get services for a business
  getServices: async (businessId, params = {}) => {
    try {
      const response = await api.get(`/businesses/${businessId}/services/`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching services:', error);
      throw error;
    }
  },

  // Get a single service
  getService: async (businessId, serviceId) => {
    try {
      const response = await api.get(`/businesses/${businessId}/services/${serviceId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching service:', error);
      throw error;
    }
  },

  // Create a new service. Uses multipart/form-data so image files attach correctly.
  createService: async (businessId, serviceData) => {
    try {
      const formData = new FormData();
      Object.keys(serviceData).forEach((key) => {
        const value = serviceData[key];
        if (key === 'images' && Array.isArray(value)) {
          value.forEach((file) => formData.append('images', file));
        } else if (key === 'is_available' || key === 'booking_required') {
          formData.append(key, value ? 'true' : 'false');
        } else if (value !== undefined && value !== null && value !== '') {
          formData.append(key, value);
        }
      });

      const response = await api.post(`/businesses/${businessId}/services/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      return response.data;
    } catch (error) {
      console.error('Error creating service:', error);
      throw error;
    }
  },

  // Update a service. Uses multipart/form-data to keep parity with create.
  updateService: async (businessId, serviceId, serviceData) => {
    try {
      const formData = new FormData();
      Object.keys(serviceData).forEach((key) => {
        const value = serviceData[key];
        if (key === 'images' && Array.isArray(value)) {
          value.forEach((file) => formData.append('images', file));
        } else if (key === 'is_available' || key === 'booking_required') {
          formData.append(key, value ? 'true' : 'false');
        } else if (value !== undefined && value !== null && value !== '') {
          formData.append(key, value);
        }
      });

      const response = await api.put(`/businesses/${businessId}/services/${serviceId}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      return response.data;
    } catch (error) {
      console.error('Error updating service:', error);
      throw error;
    }
  },

  // Partially update a service (JSON PATCH — e.g. inline availability edits)
  patchService: async (businessId, serviceId, partialData) => {
    try {
      const response = await api.patch(`/businesses/${businessId}/services/${serviceId}/`, partialData);
      return response.data;
    } catch (error) {
      console.error('Error updating service:', error);
      throw error;
    }
  },

  // Delete a service
  deleteService: async (businessId, serviceId) => {
    try {
      const response = await api.delete(`/businesses/${businessId}/services/${serviceId}/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting service:', error);
      throw error;
    }
  },

  // Admin approval methods
  getPendingBusinesses: async () => {
    try {
      const response = await api.get('/businesses/admin/pending/');
      return response.data;
    } catch (error) {
      console.error('Error fetching pending businesses:', error);
      throw error;
    }
  },

  approveBusiness: async (businessId) => {
    try {
      const response = await api.post(`/businesses/admin/approve/${businessId}/`);
      return response.data;
    } catch (error) {
      console.error('Error approving business:', error);
      throw error;
    }
  },

  rejectBusiness: async (businessId) => {
    try {
      const response = await api.post(`/businesses/admin/reject/${businessId}/`);
      return response.data;
    } catch (error) {
      console.error('Error rejecting business:', error);
      throw error;
    }
  },

  getBusinessReviews: async (slugOrId) => {
    try {
      const response = await api.get(`/businesses/${slugOrId}/reviews/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching business reviews:', error);
      throw error;
    }
  },

  createBusinessReview: async (slugOrId, reviewData) => {
    try {
      const response = await api.post(`/businesses/${slugOrId}/reviews/`, reviewData);
      return response.data;
    } catch (error) {
      console.error('Error creating business review:', error);
      throw error;
    }
  },
};

export { businessService };